// @ts-nocheck
/**
 * Client API simplifié pour l'app web-admin
 * Évite les headers complexes qui peuvent causer des problèmes CORS
 */

// Déterminer l'URL de l'API de manière intelligente
// Utilise uniquement API_BASE_URL (exposé via VITE_API_BASE_URL pour Vite)
function getApiBaseUrl(): string {
  // Vérifier la variable d'environnement Vite (définie automatiquement depuis API_BASE_URL dans docker-compose)
  if (import.meta?.env?.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }

  // Fallback par défaut pour le développement local
  return 'http://localhost:3000/api/v1';
}

const API_BASE_URL = getApiBaseUrl();

// Callback pour les erreurs d'authentification
let authErrorCallback: ((error: string) => void) | null = null;

export const setAuthErrorCallback = (callback: (error: string) => void) => {
  authErrorCallback = callback;
};

console.log('🔧 Simple API Client Configuration:', {
  API_BASE_URL,
  NODE_ENV: import.meta?.env?.NODE_ENV,
  currentHost: typeof window !== 'undefined' ? window.location.hostname : 'server-side'
});

export class SimpleApiClient {
  private baseUrl: string;
  private accessToken?: string;
  private refreshToken?: string;

  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  setTokens(accessToken: string, refreshToken?: string) {
    console.log('🔑 Setting tokens in Simple API client');
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
  }

  clearTokens() {
    console.log('🔓 Clearing tokens from Simple API client');
    this.accessToken = undefined;
    this.refreshToken = undefined;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (this.accessToken) {
      headers.Authorization = `Bearer ${this.accessToken}`;
    }

    console.log('🌐 Simple API Request:', {
      url,
      method: options.method || 'GET',
      body: options.body,
    });

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      console.log('📡 Simple API Response:', {
        status: response.status,
        ok: response.ok,
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('❌ Simple API Error:', error);
        
        // Détecter les erreurs d'authentification
        if (response.status === 401 || response.status === 403) {
          const errorMessage = error.message || 'Session expirée. Veuillez vous reconnecter.';
          console.warn('🔐 Authentication error detected:', errorMessage);
          if (authErrorCallback) {
            authErrorCallback(errorMessage);
          }
        }
        
        throw new Error(error.message || `HTTP ${response.status}`);
      }

      const data = await response.json();
      console.log('📦 Simple API Data:', data);
      
      // Retourner dans le même format que l'ApiClient original
      return { data, success: true };
    } catch (error) {
      console.error('💥 Simple API Request Error:', error);
      throw error;
    }
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.makeRequest<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.makeRequest<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.makeRequest<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T>(endpoint: string, data?: any): Promise<T> {
    return this.makeRequest<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.makeRequest<T>(endpoint, { method: 'DELETE' });
  }
}

export const simpleApiClient = new SimpleApiClient();
